import classNames from "classnames";

import {
  ChartColumn,
  LayoutDashboard,
  Map as MapIcon,
  PanelTop,
  Square,
  SquareMousePointer,
  Table2,
  type LucideIcon,
  EntityIcon,
  type EntityType,
} from "@/shared/foundation/icons";

import { type ContextType } from "../contextType";

/**
 * What a context chip can show an icon for: every context type, plus items
 * that sit in a type's group but take a glyph of their own:
 * - the View loaded on a table, which shares the table's chip but not its
 *   glyph;
 * - a section, or a panel of each kind, selected on a board's canvas.
 */
export type ContextIconKind =
  | ContextType
  | "view"
  | "board_section"
  | "chart_panel"
  | "table_panel"
  | "map_panel"
  | "panel";

type RecordKind = Extract<
  ContextIconKind,
  | "dataset"
  | "file"
  | "collection"
  | "device"
  | "deviceType"
  | "session"
  | "table"
  | "view"
  | "viz_state"
>;

/**
 * The record type each record chip stands for. A table's rows are a topic's
 * data, and a visualizer state is a visualization.
 */
const CONTEXT_ENTITY_TYPES: Record<RecordKind, EntityType> = {
  dataset: "dataset",
  file: "file",
  collection: "collection",
  device: "device",
  deviceType: "deviceType",
  session: "session",
  table: "topic",
  view: "view",
  viz_state: "visualization",
};

/** Boards and what is selected on their canvas, which aren't records. */
const BOARD_ICONS: Record<Exclude<ContextIconKind, RecordKind>, LucideIcon> = {
  board: LayoutDashboard,
  selected_on_canvas: SquareMousePointer,
  board_section: PanelTop,
  chart_panel: ChartColumn,
  table_panel: Table2,
  map_panel: MapIcon,
  panel: Square,
};

interface ContextIconProps {
  contextType: ContextIconKind;
  className?: classNames.Argument;
}

/**
 * Context icon component for a given context type: a record's icon from
 * ENTITY_ICONS, or a board item's own glyph.
 */
export function ContextIcon(props: ContextIconProps) {
  const { contextType, className } = props;
  if (contextType in CONTEXT_ENTITY_TYPES) {
    return (
      <EntityIcon
        type={CONTEXT_ENTITY_TYPES[contextType as RecordKind]}
        className={classNames(className)}
      />
    );
  }
  const Icon = BOARD_ICONS[contextType as Exclude<ContextIconKind, RecordKind>];
  return <Icon className={classNames(className)} />;
}
